#!/usr/bin/env node
import { Command } from 'commander';
import chalk from 'chalk';
import { initCommand } from './commands/init.js';
import { checkCommand } from './commands/check.js';
import { explainCommand } from './commands/explain.js';
import { ciCommand } from './commands/ci.js';
import { autofixCommand } from './commands/autofix.js';
import { baselineCommand } from './commands/baseline.js';
import { fixCommand } from './commands/fix.js';
import { watchCommand } from './commands/watch.js';
import { reportCommand } from './commands/report.js';

const program = new Command();

program
  .name('dr')
  .description('Devrail - Security & Quality Guardrails')
  .version('0.1.0');

program
  .command('init')
  .description('Initialize Devrail in your project')
  .option('-p, --preset <preset>', 'Preset to use (auto-detected if not specified)')
  .option('-l, --level <level>', 'Level (basic, standard, strict)', 'standard')
  .option('--no-hooks', 'Skip git hooks setup')
  .option('--no-ci', 'Skip CI template generation')
  .action(initCommand);

program
  .command('check')
  .description('Run local checks (fast)')
  .option('-f, --fix', 'Apply safe autofixes')
  .option('--changed', 'Only check changed files')
  .option('--json', 'Output as JSON')
  .action(checkCommand);

program
  .command('ci')
  .description('Run full CI checks (blocking)')
  .option('--fail-on <severity>', 'Fail on severity level (info, warn, error)', 'error')
  .option('--format <format>', 'Output format (console, json, sarif)', 'console')
  .option('--new-only', 'Only fail on NEW issues (not baselined)', true)
  .option('--no-new-only', 'Fail on ALL issues (ignore baseline)')
  .option('--diff-only', 'Only check changed files')
  .action(ciCommand);

program
  .command('explain <ruleId>')
  .description('Explain a rule and how to fix it')
  .action(explainCommand);

program
  .command('fix')
  .description('Apply safe auto-fixes')
  .option('--dry-run', 'Show what would be fixed without applying')
  .option('--all', 'Include fixes that need review')
  .action(fixCommand);

program
  .command('baseline')
  .description('Accept existing issues, block only new ones')
  .option('--update', 'Update existing baseline with current issues')
  .option('--show', 'Show current baseline status')
  .action(baselineCommand);

program
  .command('guard')
  .alias('watch')
  .description('Watch mode - continuous feedback while coding')
  .option('--debounce <ms>', 'Debounce interval in milliseconds', '500')
  .action(watchCommand);

program
  .command('report')
  .description('Generate security posture report')
  .option('--format <format>', 'Output format (console, json, markdown)', 'console')
  .option('-o, --output <file>', 'Output file path')
  .action(reportCommand);

program
  .command('rules')
  .description('List all available rules')
  .option('-c, --category <category>', 'Filter by category')
  .option('-l, --level <level>', 'Filter by level')
  .action(async (options) => {
    const { rules } = await import('../rules/index.js');

    let filtered = rules;

    if (options.category) {
      filtered = filtered.filter((r) => r.category === options.category);
    }

    if (options.level) {
      filtered = filtered.filter((r) => r.levels[options.level as keyof typeof r.levels]);
    }

    console.log(chalk.bold('\nDevrail Rules\n'));

    const categories = [...new Set(filtered.map((r) => r.category))];

    for (const category of categories) {
      console.log(chalk.cyan.bold(`\n${category.toUpperCase()}`));
      const categoryRules = filtered.filter((r) => r.category === category);

      for (const rule of categoryRules) {
        const severityColor =
          rule.severity === 'error' ? chalk.red : rule.severity === 'warn' ? chalk.yellow : chalk.blue;
        const autofix = rule.autofix ? chalk.green(' [autofix]') : '';
        console.log(`  ${severityColor('●')} ${chalk.bold(rule.id)}${autofix}`);
        console.log(`    ${chalk.dim(rule.description)}`);
      }
    }

    console.log(`\n${chalk.dim(`Total: ${filtered.length} rules`)}\n`);
  });

program.parse();
